import Header from '@components/ScreenHeader'
import { SmCardView } from '@components/SmCardView'
import { Spacer } from '@components/Spacer'
import { useUserOrgs } from '@features/organizations/hooks/useUserOrgs'
import { useNav } from '@features/web-navigation/hooks/useNav'
import { cdnUrlFromPath } from '@lib/utils/cdnUrlFromPath'
import { FontAwesomeIcon, findIconDefinition } from '@purposity/icons'
import * as Colors from '@themes/Colors'
import * as React from 'react'
import {
  Platform,
  Pressable,
  Modal as RNModal,
  StyleSheet,
  Text,
  TextStyle,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native'
import { GroupedVirtuoso } from 'react-virtuoso'
import { useRouter } from 'solito/router'
import { useFilteredOrgs } from '../hooks/useOrganizations'

type WebStyle = {
  transform?: string
  boxShadow?: string
  transition?: string
  backdropFilter?: string
  cursor?: 'pointer'
  ':hover'?: {
    transform?: string
    boxShadow?: string
    opacity?: number
  }
}

type WebStyles = {
  createNeedButtonHovered: WebStyle
  createOrgButton: WebStyle
  modalContent: WebStyle
  modalButton: WebStyle
  adminLinkText: WebStyle
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.borderGray,
  },
  headerContainer: {
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.dotGray,
  },
  listContainer: {
    flex: 1,
  },
  itemContainer: {
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  itemContainerFirst: {
    paddingTop: 12,
  },
  sectionSeparator: {
    height: 1,
    marginVertical: 0,
    marginHorizontal: 'auto',
    width: 'calc(100% - 12px)',
    alignSelf: 'center',
    backgroundColor: Colors.dotGray,
    opacity: 0.25,
  },
  createNeedButtonHovered: Platform.select({
    web: {
      opacity: 0.9,
      transform: [{ translateY: -1 }],
    },
    default: {
      opacity: 0.9,
    },
  }),
  createOrgButton: {
    height: 24,
    paddingHorizontal: 12,
    borderRadius: 20,
    backgroundColor: Colors.purple,
    borderColor: Colors.purple,
    borderWidth: 1,
    minWidth: 80,
    marginRight: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContent: Platform.select({
    web: {
      backgroundColor: Colors.white,
      borderRadius: 16,
      padding: 24,
      width: '100%',
      maxWidth: 400,
      position: 'relative',
      margin: 'auto',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 20 },
      shadowOpacity: 0.15,
      shadowRadius: 60,
    },
    default: {
      backgroundColor: Colors.white,
      borderRadius: 16,
      padding: 24,
      width: '100%',
      maxWidth: 400,
      position: 'relative',
      margin: 'auto',
    },
  }),
  modalTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#1D1D1F',
    marginBottom: 12,
    textAlign: 'center',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  modalSubheader: {
    fontSize: 16,
    color: '#86868B',
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  modalButtons: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  modalButton: Platform.select({
    web: {
      width: '100%',
      height: 36,
      borderRadius: 18,
      minWidth: 200,
      maxWidth: 280,
      alignSelf: 'center',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 32,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 12,
    },
    default: {
      width: '100%',
      height: 36,
      borderRadius: 18,
      minWidth: 200,
      maxWidth: 280,
      alignSelf: 'center',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 32,
    },
  }),
  modalButtonText: {
    color: Colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 20,
    color: '#86868B',
    lineHeight: 28,
    textAlign: 'center',
  },
  modalLink: {
    color: Colors.primary,
    fontSize: 14,
    textAlign: 'center',
    textDecorationLine: 'underline',
  },
  adminLinkText: Platform.select({
    web: {
      color: Colors.primary,
      fontSize: 14,
      textAlign: 'center',
      textDecorationLine: 'underline',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      userSelect: 'none' as const,
    },
    default: {
      color: Colors.primary,
      fontSize: 14,
      textAlign: 'center',
      textDecorationLine: 'underline',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    },
  }),
})

const webOnlyStyles =
  Platform.OS === 'web'
    ? {
        createNeedButtonHovered: {
          transform: [{ translateY: -1 }],
          opacity: 0.9,
        } as ViewStyle,
        modalContent: {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 20 },
          shadowOpacity: 0.15,
          shadowRadius: 60,
        } as ViewStyle,
        modalButton: {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.1,
          shadowRadius: 12,
        } as ViewStyle,
        adminLinkText: {
          cursor: 'pointer' as const,
          opacity: 0.8,
        } as TextStyle,
      }
    : {}

const webHeaderStyle =
  Platform.OS === 'web'
    ? {
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }
    : {}

function CreateNeedButton({ isMobile }: { isMobile: boolean }) {
  const [modalVisible, setModalVisible] = React.useState(false)

  const buttonStyles = {
    backgroundColor: Colors.white,
    height: isMobile ? 24 : 32,
    paddingHorizontal: isMobile ? 8 : 12,
    borderRadius: isMobile ? 12 : 16,
    minWidth: isMobile ? 'auto' : 90,
    marginRight: isMobile ? 4 : 16,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
  }

  const iconStyles = {
    width: isMobile ? 16 : 20,
    height: isMobile ? 16 : 20,
    borderRadius: isMobile ? 8 : 10,
    marginRight: isMobile ? 4 : 6,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    borderWidth: 1.5,
    borderColor: Colors.newPurple,
    backgroundColor: Colors.white,
  }

  const textStyles = {
    fontSize: isMobile ? 11 : 14,
    letterSpacing: 0.5,
    color: Colors.newPurple,
    fontWeight: '700' as const,
    textTransform: 'uppercase' as const,
  }

  return (
    <>
      <Pressable
        style={({ pressed, hovered }) => [
          buttonStyles,
          (pressed || hovered) && styles.createNeedButtonHovered,
          (pressed || hovered) && webOnlyStyles.createNeedButtonHovered,
        ]}
        onPress={() => setModalVisible(true)}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={iconStyles}>
            <FontAwesomeIcon
              icon={findIconDefinition({ prefix: 'fas', iconName: 'plus' })}
              color={Colors.newPurple}
              style={{
                width: isMobile ? 9 : 13,
                height: isMobile ? 9 : 13,
              }}
            />
          </View>
          <Text style={textStyles}>Create Org</Text>
        </View>
      </Pressable>

      <RNModal
        animationType="fade"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, webOnlyStyles.modalContent]}>
            <Pressable
              style={styles.closeButton}
              onPress={() => setModalVisible(false)}
            >
              <FontAwesomeIcon
                icon={findIconDefinition({ prefix: 'fas', iconName: 'times' })}
                color={Colors.black}
                style={{ width: 16, height: 16 }}
              />
            </Pressable>

            <Text style={styles.modalTitle}>Submit Needs</Text>
            <Text style={styles.modalSubheader}>
              Get approved to submit needs for your organization.
            </Text>

            <Pressable
              style={[styles.modalButton, webOnlyStyles.modalButton]}
              onPress={() => {
                window.open(
                  'https://docs.google.com/forms/d/e/1FAIpQLSdP8ZzHGyheSKKlUF2i9TDE7OUG4Kbe8GJyzGyyD4LgJCEtEg/viewform',
                  '_blank'
                )
                setModalVisible(false)
              }}
            >
              <Text style={styles.modalButtonText}>Complete Agreement</Text>
            </Pressable>

            <Pressable
              onPress={() => {
                window.open('https://admin.purposity.com', '_blank')
                setModalVisible(false)
              }}
            >
              <Text style={[styles.modalLink, webOnlyStyles.adminLinkText]}>
                Log in to submit needs here
              </Text>
            </Pressable>
          </View>
        </View>
      </RNModal>
    </>
  )
}

function HeaderRight({
  canGoBack,
  tintColor,
}: {
  canGoBack: boolean
  tintColor?: string
}) {
  const { width } = useWindowDimensions()
  const isMobile = width <= 768

  return (
    <Pressable
      style={({ pressed, hovered }) => [
        {
          backgroundColor: Colors.white,
          height: isMobile ? 24 : 32,
          paddingHorizontal: isMobile ? 8 : 12,
          borderRadius: isMobile ? 12 : 16,
          minWidth: isMobile ? 'auto' : 90,
          marginRight: isMobile ? 4 : 16,
          justifyContent: 'center' as const,
          alignItems: 'center' as const,
        },
        (pressed || hovered) && styles.createNeedButtonHovered,
        (pressed || hovered) && webOnlyStyles.createNeedButtonHovered,
      ]}
      onPress={() =>
        window.open(
          'https://forms.monday.com/forms/334f535b39f5145f0e88a2cf7fa99228?r=use1',
          '_blank'
        )
      }
    >
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <View
          style={{
            width: isMobile ? 16 : 20,
            height: isMobile ? 16 : 20,
            borderRadius: isMobile ? 8 : 10,
            marginRight: isMobile ? 4 : 6,
            justifyContent: 'center' as const,
            alignItems: 'center' as const,
            borderWidth: 1.5,
            borderColor: Colors.newPurple,
            backgroundColor: Colors.white,
          }}
        >
          <FontAwesomeIcon
            icon={findIconDefinition({ prefix: 'fas', iconName: 'plus' })}
            color={Colors.newPurple}
            style={{
              width: isMobile ? 9 : 13,
              height: isMobile ? 9 : 13,
            }}
          />
        </View>
        <Text
          style={{
            fontSize: isMobile ? 11 : 14,
            letterSpacing: 0.5,
            color: Colors.newPurple,
            fontWeight: '700' as const,
            textTransform: 'uppercase' as const,
          }}
        >
          Create Org
        </Text>
      </View>
    </Pressable>
  )
}

function OrganizationsScreen() {
  const [orgQuery, setOrgQuery] = React.useState<string>('')
  const userOrgsQuery = useUserOrgs()
  const orgsQuery = useFilteredOrgs({ query: orgQuery })
  const orgs = orgsQuery.data
  type Org = NonNullable<NonNullable<typeof orgs>[number]>

  const { groups, groupCounts } = React.useMemo(() => {
    if (!orgs) return { groups: [], groupCounts: [] }

    const memberOrgs: typeof orgs = []
    const otherOrgs: typeof orgs = []
    const userOrgIds = userOrgsQuery.data?.map((org) => org.id) || []

    orgs.forEach((org) => {
      if (userOrgIds.includes(org.id)) {
        memberOrgs.push(org)
      } else {
        otherOrgs.push(org)
      }
    })

    return {
      groups: [memberOrgs, otherOrgs],
      groupCounts: [memberOrgs.length, otherOrgs.length],
    }
  }, [orgs, userOrgsQuery.data])

  const lazyRoot = React.useRef<HTMLElement | Window | null>(null)
  const router = useRouter()
  const nav = useNav()
  return (
    <View style={styles.container}>
      <View style={[styles.headerContainer, webHeaderStyle]}>
        <Header
          title="Organizations"
          backAction={nav.isLt768 ? () => router.back() : undefined}
          searchConfig={{
            showSearch: true,
            onSearch: (value) => setOrgQuery(value || ''),
            searchPlaceholder: 'Search organizations...',
          }}
          headerRight={HeaderRight}
        />
      </View>

      <View style={styles.listContainer}>
        <GroupedVirtuoso
          style={{ height: '100%' }}
          groupCounts={groupCounts}
          groupContent={(index) =>
            index >= 1 ? (
              <>
                <Spacer noShrink height={8} />
                <View style={styles.sectionSeparator} />
                <Spacer noShrink height={8} />
              </>
            ) : (
              <Spacer noShrink height={8} />
            )
          }
          context={{
            userOrgs: userOrgsQuery.data,
          }}
          itemContent={(_index, groupIndex, _, context) => {
            const group = groups[groupIndex]
            if (!group) return null
            const itemData = group[_index]
            if (!itemData) return null

            const count = itemData.stats?.needs_available_count
            const subText = !count
              ? 'No needs currently available'
              : `${count} need${count === 1 ? '' : 's'} available`

            return (
              <View
                style={[
                  styles.itemContainer,
                  _index === 0 && styles.itemContainerFirst,
                ]}
              >
                <SmCardView
                  id={itemData.id}
                  name={itemData?.name}
                  href={`/organization/${itemData.alias}`}
                  subText={subText}
                  imageUrl={cdnUrlFromPath(itemData?.profile_image_path)}
                  showCheck={
                    !!context.userOrgs?.some?.(
                      (item: { id: number }) => item.id === itemData.id
                    )
                  }
                />
              </View>
            )
          }}
        />
      </View>
    </View>
  )
}

export default OrganizationsScreen
